import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';

import defaultClient from '@src/api/clients/default';
import { toast } from '@src/components/core/toaster';
import config from '@src/config';
import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import paths from '@src/paths';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer (read-only dashboards)' },
  { value: 'editor', label: 'Editor (can update onboarding + assets)' },
  { value: 'admin', label: 'Admin (full access for this client)' },
];

const INITIAL_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  role: ROLE_OPTIONS[0].value,
};

function ClientMemberInvitePage() {
  const user = useEffectiveUser();
  const isStaff = user?.is_staff || user?.is_superuser;
  const [searchParams] = useSearchParams();

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedParentCompany, setSelectedParentCompany] = useState(null);
  const [inviteScope, setInviteScope] = useState('client');
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const prefillApplied = useRef(false);
  const lastPayloadRef = useRef(null);
  const {
    options: clientOptions,
    loading: clientSearchLoading,
    handleInputChange: handleClientSearchInput,
  } = useClientAutocomplete();

  // Pre-fill form fields from URL query params (e.g. from approval email link)
  useEffect(() => {
    if (prefillApplied.current) return;
    const qEmail = searchParams.get('email');
    const qRole = searchParams.get('role');
    const qFirstName = searchParams.get('first_name');
    const qLastName = searchParams.get('last_name');
    if (!qEmail && !qRole) return;

    prefillApplied.current = true;
    setForm((prev) => ({
      ...prev,
      ...(qEmail ? { email: qEmail } : {}),
      ...(qRole && ROLE_OPTIONS.some((o) => o.value === qRole) ? { role: qRole } : {}),
      ...(qFirstName ? { firstName: qFirstName } : {}),
      ...(qLastName ? { lastName: qLastName } : {}),
    }));

    const qParentCompanyId = searchParams.get('parent_company_id');
    if (qParentCompanyId) {
      setInviteScope('parent');
    }
  }, [searchParams]);

  // Auto-select client from query param once client options load
  useEffect(() => {
    const qClientId = searchParams.get('client_id');
    if (!qClientId || selectedClient) return;
    const match = clientOptions.find((opt) => String(opt.value) === String(qClientId));
    if (match) {
      setSelectedClient(match);
    }
  }, [clientOptions, searchParams, selectedClient]);

  // Auto-select parent company from query param once clients load
  useEffect(() => {
    const qParentCompanyId = searchParams.get('parent_company_id');
    if (!qParentCompanyId || selectedParentCompany) return;
    const pcId = parseInt(qParentCompanyId, 10);
    const match = clients.find((c) => c?.parent_company === pcId);
    if (match) {
      setSelectedParentCompany({
        id: match.parent_company,
        name: match.parent_company_name || `Parent ${match.parent_company}`,
      });
    }
  }, [clients, searchParams, selectedParentCompany]);

  useEffect(() => {
    if (!isStaff) return;
    setClientsLoading(true);
    defaultClient
      .request({
        url: 'dashboard/clients/',
        method: 'GET',
        payload: {
          fields: 'id,name,parent_company,parent_company_name',
        },
      })
      .then((response) => {
        const list = Array.isArray(response?.data?.results)
          ? response.data.results
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setClients(list);
      })
      .catch(() => {
        setClients([]);
        toast.error('Could not load clients. Please retry.');
      })
      .finally(() => setClientsLoading(false));
  }, [isStaff]);

  const sortedClients = useMemo(() => {
    return [...clientOptions].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [clientOptions]);

  const parentCompanyOptions = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      if (client?.parent_company) {
        const key = String(client.parent_company);
        if (!map.has(key)) {
          map.set(key, {
            id: client.parent_company,
            name: client.parent_company_name || `Parent ${client.parent_company}`,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const loginUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    return `${origin}${config.LOGIN_URL}`;
  }, []);

  const handleInputChange = useCallback((field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  }, []);

  const handleScopeChange = useCallback((event) => {
    const value = event.target.value;
    setInviteScope(value);
    setSelectedClient(null);
    setSelectedParentCompany(null);
    setLastInvite(null);
    setInviteError('');
  }, []);

  const handleCopyLogin = useCallback(() => {
    const link = loginUrl || config.LOGIN_URL;
    if (!navigator?.clipboard || !link) {
      toast.error('Clipboard is not available in this browser.');
      return;
    }
    navigator.clipboard.writeText(link).then(
      () => toast.success('Login link copied'),
      () => toast.error('Unable to copy link')
    );
  }, [loginUrl]);

  const sendInvite = useCallback((payload, scopeMeta) => {
    setSending(true);
    setInviteError('');
    setLastInvite(null);

    defaultClient
      .request({
        url: 'accounts/auth/client-memberships/invite/',
        method: 'POST',
        payload,
      })
      .then((response) => {
        const inviteEmailSent = response?.data?.invite_email_sent !== false;
        const userAlreadyMember = Boolean(response?.data?.user_already_member);
        const membershipCreated = Boolean(response?.data?.membership_created);
        lastPayloadRef.current = { payload, scopeMeta };
        setLastInvite({
          membership: response?.data || null,
          email: payload.email,
          clientName: scopeMeta.clientName,
          scope: scopeMeta.scope,
          inviteEmailSent,
          userAlreadyMember,
          membershipCreated,
        });
        if (inviteEmailSent) {
          toast.success('Invite sent. We emailed them a link to join.');
        } else if (membershipCreated) {
          toast.error('User was added to this client, but invite email was not sent (they already logged in before).');
        } else if (userAlreadyMember) {
          toast.error('User already exists and already has access. Invite email was not sent.');
        } else {
          toast.error('User already exists. Invite email was not sent.');
        }
        setForm(INITIAL_FORM);
      })
      .catch((error) => {
        const code = error?.response?.data?.code;
        const existingClientName = error?.response?.data?.existing_client_name;
        const detail = error?.response?.data?.detail;
        if (code === 'existing_client_membership_conflict') {
          const message = existingClientName
            ? `Invite blocked: this user already has single-client access for ${existingClientName}. Remove that access first.`
            : 'Invite blocked: this user already has single-client access to another client. Remove that access first.';
          setInviteError(message);
          toast.error(message);
          return;
        }
        if (detail) {
          setInviteError(detail);
          toast.error(detail);
          return;
        }
        setInviteError('Unable to send invite. Please try again.');
        toast.error('Unable to send invite. Please try again.');
      })
      .finally(() => setSending(false));
  }, []);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!isStaff) {
        toast.error('Only staff can invite new client members.');
        return;
      }
      if (inviteScope === 'client' && !selectedClient?.value) {
        toast.error('Select a client to invite this user to.');
        return;
      }
      if (inviteScope === 'parent' && !selectedParentCompany?.id) {
        toast.error('Select a parent company to invite this user to.');
        return;
      }
      if (!form.email.trim()) {
        toast.error('Email is required.');
        return;
      }

      const payload = {
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        role: form.role,
      };
      if (inviteScope === 'client') {
        payload.client_id = selectedClient.value;
      } else {
        payload.parent_company_id = selectedParentCompany.id;
      }

      sendInvite(payload, {
        scope: inviteScope,
        clientName:
          inviteScope === 'client' ? selectedClient.label : selectedParentCompany.name,
      });
    },
    [form, inviteScope, isStaff, selectedClient, selectedParentCompany, sendInvite]
  );

  const handleResendLast = useCallback(() => {
    const last = lastPayloadRef.current;
    if (!last) return;
    sendInvite(last.payload, last.scopeMeta);
  }, [sendInvite]);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isStaff) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This page is only available to staff users. Client members cannot invite other users.
        </Alert>
        <Button variant="contained" href={paths.dashboard.dailyPulse}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Invite client members</Typography>
        <Typography color="text.secondary">
          Create a user (or re-activate existing), add them to a client or parent company, and send them a password setup email that lands on the login screen.
        </Typography>
      </Stack>

      <Card component="form" onSubmit={handleSubmit}>
        <CardContent>
          <Stack spacing={3}>
            {clientsLoading && <LinearProgress />}
            <Stack spacing={1}>
              <Typography variant="subtitle2">Scope</Typography>
              <TextField
                label="Invite scope"
                value={inviteScope}
                onChange={handleScopeChange}
                select
                fullWidth
              >
                <MenuItem value="client">Single client</MenuItem>
                <MenuItem value="parent">Parent company</MenuItem>
              </TextField>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {inviteScope === 'client' ? 'Client' : 'Parent company'}
              </Typography>
              {inviteScope === 'client' ? (
                <Autocomplete
                  value={selectedClient}
                  onChange={(_, value) => setSelectedClient(value)}
                  onInputChange={handleClientSearchInput}
                  loading={clientSearchLoading}
                  options={sortedClients}
                  isOptionEqualToValue={(option, value) => option.value === value?.value}
                  getOptionLabel={(option) => option?.label || ''}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.label}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search clients"
                      placeholder="Start typing a client name"
                      InputProps={{
                        ...params.InputProps,
                              endAdornment: (
                                <>
                                  {clientSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                      }}
                      required
                    />
                  )}
                  fullWidth
                />
              ) : (
                <Autocomplete
                  value={selectedParentCompany}
                  onChange={(_, value) => setSelectedParentCompany(value)}
                  options={parentCompanyOptions}
                  getOptionLabel={(option) => option?.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search parent companies"
                      placeholder="Start typing a parent company"
                      required
                    />
                  )}
                  fullWidth
                />
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={handleInputChange('firstName')}
                fullWidth
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={handleInputChange('lastName')}
                fullWidth
              />
            </Stack>

            <TextField
              label="Email"
              value={form.email}
              onChange={handleInputChange('email')}
              type="email"
              required
              fullWidth
            />

            <TextField
              label="Role"
              value={form.role}
              onChange={handleInputChange('role')}
              select
              helperText={
                inviteScope === 'client'
                  ? 'Roles apply only to this client.'
                  : 'Roles apply to the entire parent company.'
              }
              fullWidth
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SendIcon />}
                disabled={sending}
              >
                {sending ? 'Sending invite…' : 'Send invite'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                We&apos;ll create or update the user, attach them to the selected scope, and email them a setup link
                (valid 30 days). Inviting the same person again re-sends a fresh link as long as they haven&apos;t set
                a password yet.
              </Typography>
            </Stack>

            {inviteError ? <Alert severity="error">{inviteError}</Alert> : null}

            {lastInvite ? (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Alert
                  severity={lastInvite.inviteEmailSent ? 'success' : 'error'}
                  action={
                    <Stack direction="row" spacing={1}>
                      {lastInvite.inviteEmailSent ? (
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<ReplayIcon fontSize="small" />}
                          onClick={handleResendLast}
                          disabled={sending}
                        >
                          {sending ? 'Resending…' : 'Resend email'}
                        </Button>
                      ) : null}
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={handleCopyLogin}
                      >
                        Copy login link
                      </Button>
                    </Stack>
                  }
                >
                  {lastInvite.inviteEmailSent ? 'Invite sent to ' : 'No invite email sent for '}
                  <strong>{lastInvite.email}</strong> for <strong>{lastInvite.clientName}</strong>.
                  {lastInvite.inviteEmailSent ? (
                    ' The setup link stays valid for 30 days, and they can also request a fresh one from the expired-link page. They can log in at ' +
                    `${loginUrl || config.LOGIN_URL} after setting a password.`
                  ) : lastInvite.membershipCreated ? (
                    ' Access was granted to this client, but this user has logged in before so we did not send a new invite email.'
                  ) : (
                    ' This user already had access and has logged in before, so we did not send a new invite email.'
                  )}
                </Alert>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

ClientMemberInvitePage.displayName = 'ClientMemberInvitePage';

export default ClientMemberInvitePage;
